import { FC } from "react";

import { Stack, Typography } from "@mui/material";
import { GOOGLE_MAP_ID, GoogleMapsProvider, useMapColorScheme } from "@rubi/ui";
import { AdvancedMarker, Map, useApiIsLoaded } from "@vis.gl/react-google-maps";

export interface ParticipantLocation {
  id: string;
  name: string;
  lat: number;
  lng: number;
}

interface Props {
  points: ParticipantLocation[];
  height: number;
}

const ParticipantLocationsMapContent: FC<Props> = ({ points, height }) => {
  const mapColorScheme = useMapColorScheme();
  const apiIsLoaded = useApiIsLoaded();

  // Coordinates stay readable without the API — no key configured, or still loading.
  if (!apiIsLoaded) {
    return (
      <Stack spacing={0.5}>
        {points.map((p) => (
          <Typography key={p.id} variant="caption">
            {p.name}:{" "}
            <a
              href={`https://maps.google.com/?q=${p.lat},${p.lng}`}
              target="_blank"
              rel="noreferrer"
            >
              {p.lat}, {p.lng}
            </a>
          </Typography>
        ))}
      </Stack>
    );
  }

  return (
    <Map
      colorScheme={mapColorScheme}
      defaultCenter={{ lat: points[0].lat, lng: points[0].lng }}
      defaultZoom={11}
      style={{ width: "100%", height }}
      mapId={GOOGLE_MAP_ID}
    >
      {points.map((p) => (
        <AdvancedMarker
          key={p.id}
          position={{ lat: p.lat, lng: p.lng }}
          title={p.name}
        />
      ))}
    </Map>
  );
};

/**
 * Mounts the Maps API for the connect dialogs. It has to sit above the content
 * rather than inside it: the load gate uses `useApiIsLoaded`, which reads React
 * context, and a component cannot consume a context it renders itself.
 *
 * Callers render this only when there are points, so a dialog with no captured
 * locations never loads the API.
 */
const ParticipantLocationsMap: FC<Props> = (props) => (
  <GoogleMapsProvider>
    <ParticipantLocationsMapContent {...props} />
  </GoogleMapsProvider>
);

export default ParticipantLocationsMap;
